import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../ui/command';
import { api } from '../../../context/AuthContext';

/**
 * Universal admin search. Loads the admin entity lists once per open and
 * filters client-side, grouping results by entity type.
 */
export const GlobalSearch = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ projects: [], parties: [], instruments: [], subscriptions: [] });

  const load = useCallback(async () => {
    const safe = (p) => p.catch(() => ({ data: {} }));
    const [projects, parties, instruments, subscriptions] = await Promise.all([
      safe(api.get('/admin/workspace/projects')),
      safe(api.get('/admin/v2/parties?limit=200')),
      safe(api.get('/admin/projects/instruments/all')),
      safe(api.get('/admin/v2/subscriptions?limit=100')),
    ]);
    setData({
      projects: projects.data.projects || [],
      parties: parties.data.parties || [],
      instruments: instruments.data.instruments || [],
      subscriptions: subscriptions.data.subscriptions || [],
    });
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search projects, investors, instruments, subscriptions…" data-testid="global-search-input" />
      <CommandList data-testid="global-search-results">
        <CommandEmpty>No matches found.</CommandEmpty>

        {data.projects.length > 0 && (
          <CommandGroup heading="Projects">
            {data.projects.map((p) => (
              <CommandItem key={p.id} value={`${p.title} ${p.code}`} onSelect={() => go(`/admin/v2/projects/${p.id}`)}>
                <span className="flex-1">{p.title}</span>
                <span className="text-xs text-foreground-muted font-mono">{p.code}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {data.parties.length > 0 && (
          <CommandGroup heading="Investors & Parties">
            {data.parties.map((p) => (
              <CommandItem
                key={p.id}
                value={`${p.display_name || p.legal_name} ${p.code || ''}`}
                onSelect={() => go('/admin/v2/parties')}
              >
                <span className="flex-1">{p.display_name || p.legal_name}</span>
                <span className="text-xs text-foreground-muted font-mono">{p.code}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {data.instruments.length > 0 && (
          <CommandGroup heading="Instruments">
            {data.instruments.map((i) => (
              <CommandItem key={i.id} value={`${i.name} ${i.code}`} onSelect={() => go('/admin/instruments')}>
                <span className="flex-1">{i.name}</span>
                <span className="text-xs text-foreground-muted font-mono">{i.code}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {data.subscriptions.length > 0 && (
          <CommandGroup heading="Subscriptions">
            {data.subscriptions.map((s) => (
              <CommandItem
                key={s.id}
                value={`${s.code} ${s.investor_name || ''} ${s.offering_name || ''}`}
                onSelect={() => go('/admin/v2/subscriptions')}
              >
                <span className="flex-1">{s.investor_name || s.offering_name || 'Subscription'}</span>
                <span className="text-xs text-foreground-muted font-mono">{s.code}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default GlobalSearch;
